import { Menu, Transition } from '@headlessui/react'
import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import {
  faArrowRightFromBracket,
  faChevronRight,
  faCog,
  faFileInvoice,
  faGearCode,
  faUser,
  faUserGroup
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'

import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'

import { NavLink } from 'components/navigation/NavLink'
import { linkClasses } from 'components/navigation/styles'

import { BILLING_PLAN_ROUTE } from 'lib/constants'

interface SettingsMenuProps {
  className?: string
}

const initClickEventProps = {
  location: ' navbar',
  type: 'Text'
}

const menuItemStyles =
  'grid grid-cols-[1.25rem_1fr] hover:text-text-default font-medium text-text-alternative items-center w-full gap-3 px-4 py-2 text-sm rounded-md'

export function SettingsMenu({ className }: SettingsMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 })

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setMenuPosition({
        // TODO: can we pass in ref for the nav container to make this positioning more accurate?
        left: rect.right + 28,

        top: rect.top - 130
      })
    }
  }

  useEffect(() => {
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [])

  const MenuItems = (
    <Menu.Items
      className='bg-background-default border-border-muted fixed z-50 overflow-hidden rounded-lg border shadow-lg'
      style={{ left: menuPosition.left, top: menuPosition.top }}
    >
      <div className='p-1'>
        <Menu.Item>
          {({ active }) => (
            <Link
              className={clsx(menuItemStyles, active ? 'bg-background-default-hover' : '')}
              href='/settings/account'
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...initClickEventProps,
                  clickText: 'Account',
                  clickUrl: '/settings/account'
                })
              }}
            >
              <FontAwesomeIcon className='hover:text-text-default text-text-alternative' icon={faUser} />
              Account
            </Link>
          )}
        </Menu.Item>

        <Menu.Item>
          {({ active }) => (
            <Link
              className={clsx(menuItemStyles, active ? 'bg-background-default-hover' : '')}
              href={BILLING_PLAN_ROUTE}
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...initClickEventProps,
                  clickText: 'Billing',
                  clickUrl: BILLING_PLAN_ROUTE
                })
              }}
            >
              <FontAwesomeIcon className='hover:text-text-default text-text-alternative' icon={faFileInvoice} />
              <span>Billing</span>
            </Link>
          )}
        </Menu.Item>

        <Menu.Item>
          {({ active }) => (
            <Link
              className={clsx(menuItemStyles, active ? 'bg-background-default-hover' : '')}
              href='/settings/sharing'
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...initClickEventProps,
                  clickText: 'Key Sharing',
                  clickUrl: '/settings/sharing'
                })
              }}
            >
              <FontAwesomeIcon className='hover:text-text-default text-text-alternative' icon={faUserGroup} />
              Key sharing
            </Link>
          )}
        </Menu.Item>

        <Menu.Item>
          {({ active }) => (
            <Link
              className={clsx(menuItemStyles, active ? 'bg-background-default-hover' : '')}
              data-testid='key-settings-link'
              href={`/key/settings`}
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...initClickEventProps,
                  clickText: 'Key settings',
                  clickUrl: `/key/settings`
                })
              }}
            >
              <FontAwesomeIcon className='hover:text-text-default text-text-alternative' icon={faGearCode} />
              Key settings
            </Link>
          )}
        </Menu.Item>

        <div className='bg-border-default my-1 h-px' />

        <Menu.Item>
          {({ active }) => (
            <Link
              className={`
              text-error-default' flex w-full items-center gap-3 rounded-md px-4 py-2 text-sm
              ${active ? 'bg-error-default-hover !text-black' : 'text-error-default'}
            `}
              href='/logout'
              onClick={() => {
                trackClickForSegmentAnalytics({
                  ...initClickEventProps,
                  clickText: 'Log out',
                  clickUrl: '/logout'
                })
              }}
            >
              <FontAwesomeIcon icon={faArrowRightFromBracket} />
              Log out
            </Link>
          )}
        </Menu.Item>
      </div>
    </Menu.Items>
  )

  return (
    <Menu as='div' className={className}>
      {({ open }) => (
        <>
          <Menu.Button
            className={clsx(linkClasses, 'w-full')}
            data-testid='navbar-settings-button'
            onClick={updatePosition}
            ref={buttonRef}
          >
            <FontAwesomeIcon icon={faCog} />

            <span className='flex items-center justify-between'>
              <span className='text-sm font-medium'>Settings</span>
              <FontAwesomeIcon
                className={`ml-2 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                icon={faChevronRight}
              />
            </span>
          </Menu.Button>

          <Transition
            as='div'
            enter='transition ease-out duration-100'
            enterFrom='transform opacity-0 scale-95'
            enterTo='transform opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='transform opacity-100 scale-100'
            leaveTo='transform opacity-0 scale-95'
          >
            {open ? createPortal(MenuItems, document.body) : null}
          </Transition>
        </>
      )}
    </Menu>
  )
}

// On mobile the links are not shown in a menu
export const SettingsLinks = () => {
  return (
    <>
      <NavLink
        href='/settings/account'
        icon={faUser}
        label='Account'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Account',
          clickUrl: '/settings/account'
        }}
      />
      <NavLink
        href={BILLING_PLAN_ROUTE}
        icon={faFileInvoice}
        label='Billing'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Billing',
          clickUrl: BILLING_PLAN_ROUTE
        }}
      />

      <NavLink
        href='/settings/sharing'
        icon={faUserGroup}
        label='Key Sharing'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Key Sharing',
          clickUrl: '/settings/sharing'
        }}
      />

      <NavLink
        dataTestid='key-settings-link'
        href='/key/settings'
        icon={faGearCode}
        label='Key settings'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Key settings',
          clickUrl: `/key/settings`
        }}
      />

      <NavLink
        href='/logout'
        icon={faArrowRightFromBracket}
        label='Log out'
        trackClickEvent={{
          ...initClickEventProps,
          clickText: 'Log out',
          clickUrl: '/logout'
        }}
      />
    </>
  )
}
